import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { selectUser } from "../../../core/data/redux/slice/user"
import { isTeacher } from "../../../utils/rbacHelper"
import { getAllSessions, type Session } from "../../../service/sessions"
import {
  getRubricWorkItems,
  getRubricGrid,
  saveRubricResponse,
  type RubricWorkItem,
  type RubricTemplate,
  type RubricStudentRow,
  type RubricResponseEntry,
  type RubricValue,
} from "../../../service/rubric"

// Foundation Year 1 (Eon only) developmental checklist — tri-state
// check/cross for behavioural items, 1-4 grading key for the proficiency
// items. Deliberately separate page/flow from Marks Entry: no marks, no
// totals, just this checklist per student per term.

const GRADING_KEY: { value: 1 | 2 | 3 | 4; label: string }[] = [
  { value: 1, label: "1 — Strongly Agree" },
  { value: 2, label: "2 — Agree" },
  { value: 3, label: "3 — Somewhat Disagree" },
  { value: 4, label: "4 — Disagree" },
]

const Fy1Checklist = () => {
  const user = useSelector(selectUser)
  const [sessions, setSessions] = useState<Session[]>([])
  const [sessionId, setSessionId] = useState("")
  const [workItems, setWorkItems] = useState<RubricWorkItem[]>([])
  const [loadingWorkItems, setLoadingWorkItems] = useState(false)

  const [selected, setSelected] = useState<{ work: RubricWorkItem; breakdownId: string } | null>(null)
  const [template, setTemplate] = useState<RubricTemplate | null>(null)
  const [students, setStudents] = useState<RubricStudentRow[]>([])
  const [loadingGrid, setLoadingGrid] = useState(false)

  const [openStudent, setOpenStudent] = useState<RubricStudentRow | null>(null)
  const [draftResponses, setDraftResponses] = useState<Record<string, RubricValue>>({})
  const [draftRemarks, setDraftRemarks] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getAllSessions({ limit: 200, status: "active" }).then((r: any) => setSessions(r?.sessions || [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (!sessionId) { setWorkItems([]); return }
    setLoadingWorkItems(true)
    getRubricWorkItems(sessionId)
      .then((r) => setWorkItems(r?.data || []))
      .catch((e: any) => toast.error(e?.response?.data?.message || "Failed to load your sections"))
      .finally(() => setLoadingWorkItems(false))
  }, [sessionId])

  // Default to the only session if there's just one — one less click for
  // the common case (a class teacher normally has exactly one FY1 section).
  useEffect(() => {
    if (!sessionId && sessions.length === 1) setSessionId(sessions[0]._id)
  }, [sessions]) // eslint-disable-line react-hooks/exhaustive-deps

  const openSection = (work: RubricWorkItem, breakdownId: string) => {
    setSelected({ work, breakdownId })
    setTemplate(null)
    setStudents([])
    setLoadingGrid(true)
    getRubricGrid({ template: work.templateId, section: work.sectionId, breakdown: breakdownId })
      .then((r) => {
        setTemplate(r?.data?.template || null)
        setStudents(r?.data?.students || [])
      })
      .catch((e: any) => toast.error(e?.response?.data?.message || "Failed to load checklist"))
      .finally(() => setLoadingGrid(false))
  }

  const openStudentForm = (student: RubricStudentRow) => {
    setOpenStudent(student)
    const map: Record<string, RubricValue> = {}
    student.responses.forEach((r) => { map[r.itemId] = r.value })
    setDraftResponses(map)
    setDraftRemarks(student.remarks || "")
  }

  const saveStudent = async () => {
    if (!openStudent || !selected || !template) return
    setSaving(true)
    try {
      const responses: RubricResponseEntry[] = Object.entries(draftResponses).map(([itemId, value]) => ({ itemId, value }))
      await saveRubricResponse({
        enrollmentId: openStudent.enrollmentId,
        templateId: template._id,
        breakdownId: selected.breakdownId,
        responses,
        remarks: draftRemarks,
      })
      toast.success("Saved")
      setStudents((list) =>
        list.map((s) => (s.enrollmentId === openStudent.enrollmentId ? { ...s, responses, remarks: draftRemarks } : s))
      )
      setOpenStudent(null)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to save")
    } finally {
      setSaving(false)
    }
  }

  const answeredCount = (s: RubricStudentRow) => s.responses.filter((r) => r.value != null).length
  const totalItemCount = template
    ? template.categories.reduce((n, cat) => n + cat.subcategories.reduce((m, sub) => m + sub.items.length, 0), 0)
    : 0

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">FY1 Checklist</h3>
          <p className="text-muted mb-0">
            Foundation Year 1 developmental checklist — pick a term, then fill each student's checklist.
          </p>
        </div>

        {!selected && (
          <div className="card">
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Session</label>
                  <select className="form-select" value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                    <option value="">— Select session —</option>
                    {sessions.map((s) => <option key={s._id} value={s._id}>{s.title}</option>)}
                  </select>
                </div>
              </div>

              {loadingWorkItems && <p className="text-muted">Loading…</p>}
              {!loadingWorkItems && sessionId && workItems.length === 0 && (
                <p className="text-muted mb-0">
                  {isTeacher(user)
                    ? "No FY1 class-teacher assignment found for this session — you need to be the class teacher of an FY1 section at Eon."
                    : "No FY1 sections found for this branch and session."}
                </p>
              )}
              {workItems.map((w) => (
                <div key={w.sectionId} className="border rounded p-3 mb-2">
                  <div className="fw-semibold">{w.className} · Section {w.sectionName}</div>
                  <div className="text-muted small mb-2">{w.studentCount} students</div>
                  <div className="d-flex flex-wrap gap-2">
                    {w.breakdowns.length === 0 && <span className="text-muted small">No exam terms found for this class.</span>}
                    {w.breakdowns.map((b) => (
                      <button key={b.id} className="btn btn-outline-primary btn-sm" onClick={() => openSection(w, b.id)}>
                        {b.title}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selected && (
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div>
                <div className="fw-semibold">{selected.work.className} · Section {selected.work.sectionName}</div>
                <div className="text-muted small">{template?.title}</div>
              </div>
              <button className="btn btn-light btn-sm" onClick={() => setSelected(null)}>← Back</button>
            </div>
            <div className="card-body">
              {loadingGrid && <p className="text-muted">Loading…</p>}
              {!loadingGrid && (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>GR No</th>
                        <th>Name</th>
                        <th>Progress</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s) => (
                        <tr key={s.enrollmentId}>
                          <td>{s.grNo}</td>
                          <td>{s.studentName}</td>
                          <td>
                            <span className={`badge ${answeredCount(s) >= totalItemCount && totalItemCount > 0 ? "bg-success-transparent text-success" : "bg-warning-transparent text-warning"}`}>
                              {answeredCount(s)} / {totalItemCount}
                            </span>
                          </td>
                          <td className="text-end">
                            <button className="btn btn-outline-primary btn-sm" onClick={() => openStudentForm(s)}>Open</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {openStudent && template && (
          <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)", zIndex: 1060, overflowY: "auto" }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{openStudent.studentName}</h5>
                  <button type="button" className="btn-close" onClick={() => setOpenStudent(null)}></button>
                </div>
                <div className="modal-body">
                  {template.categories.map((cat) => (
                    <div key={cat._id} className="mb-4">
                      <h6 className="text-uppercase text-muted small fw-bold mb-2">{cat.name}</h6>
                      {cat.subcategories.map((sub) => (
                        <div key={sub._id} className="mb-3">
                          <div className="fw-semibold small mb-2">{sub.name}</div>
                          {sub.items.map((item) => (
                            <div key={item._id} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                              <span className="small">{item.description}</span>
                              {item.type === "checkbox" ? (
                                <div className="btn-group btn-group-sm" role="group">
                                  <button
                                    type="button"
                                    className={`btn ${draftResponses[item._id] === "check" ? "btn-success" : "btn-outline-success"}`}
                                    onClick={() => setDraftResponses((d) => ({ ...d, [item._id]: "check" }))}
                                  >✓</button>
                                  <button
                                    type="button"
                                    className={`btn ${draftResponses[item._id] === "cross" ? "btn-danger" : "btn-outline-danger"}`}
                                    onClick={() => setDraftResponses((d) => ({ ...d, [item._id]: "cross" }))}
                                  >✗</button>
                                </div>
                              ) : (
                                <select
                                  className="form-select form-select-sm"
                                  style={{ width: 180 }}
                                  value={draftResponses[item._id] ?? ""}
                                  onChange={(e) => setDraftResponses((d) => ({ ...d, [item._id]: e.target.value ? (Number(e.target.value) as 1 | 2 | 3 | 4) : null }))}
                                >
                                  <option value="">— Not set —</option>
                                  {GRADING_KEY.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                                </select>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                  <div className="mb-2">
                    <label className="form-label fw-semibold">General Remarks</label>
                    <textarea className="form-control" rows={3} value={draftRemarks} onChange={(e) => setDraftRemarks(e.target.value)} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setOpenStudent(null)}>Cancel</button>
                  <button className="btn btn-primary" disabled={saving} onClick={saveStudent}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Fy1Checklist
